import React, { useEffect, useState } from "react";
import { getCompletedAgentCategories } from "../auth";
import "./ThirdLevelCategorySelector.css";
import SelectableOptionGrid from "./SelectableOptionGrid";
import { elakayAutoText } from "./dataElakay";


export const THIRD_LEVEL_CATEGORY_PAGE_SIZE = 40;

const ThirdLevelCategorySelector = ({
  token,
  infoSource,
  selectedCategoryIds = [],
  onSelectionChange,
  maximumSelections,
  pageSize = THIRD_LEVEL_CATEGORY_PAGE_SIZE,
  disabled = false,
  mode = "agent-profile",
  validationMessage = "",
  searchEnabled = true,
  paginationEnabled = true,
  loadCategories = getCompletedAgentCategories
}) => {
  const tr = (key, fallback) =>
    infoSource?.laguangeElakay?.("thirdLevelCategories", infoSource.languageAPI)?.[key] || fallback;
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [serverMaximum, setServerMaximum] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const limit = maximumSelections || serverMaximum || 0;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    loadCategories(token, {
      search: debouncedSearch,
      page,
      limit: Math.min(pageSize, THIRD_LEVEL_CATEGORY_PAGE_SIZE)
    })
      .then((data) => {
        if (!active) return;
        setItems(data.items || []);
        setTotalPages(data.totalPages || 1);
        setServerMaximum(data.maximumSelections);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [token, debouncedSearch, page, pageSize, loadCategories]);

  const toggle = (id) => {
    if (disabled) return;
    if (selectedCategoryIds.includes(id)) {
      onSelectionChange(selectedCategoryIds.filter((selectedId) => selectedId !== id));
      setError("");
      return;
    }
    if (!limit || selectedCategoryIds.length >= limit) {
      setError(tr("maximumReached", elakayAutoText("h_3e9c8d5bce00e8", "Maximum {maximum} categories.")).replace(elakayAutoText("h_a4fb5cb7d2cf24", "{maximum}"), limit));
      return;
    }
    onSelectionChange([...selectedCategoryIds, id]);
    setError("");
  };

  const pageNumbers = [];
  const start = Math.max(1, page - 2);
  const end = Math.min(totalPages, start + 4);
  for (let number = start; number <= end; number += 1) pageNumbers.push(number);

  return (
    <section className={`third-category-selector mode-${mode}`}>
      <div className="third-category-header">
        <div>
          <h4>{tr("title", "Select Your Categories")}</h4>
          <p>{tr("thirdLevelOnly", "Only third-level categories are displayed.")}</p>
        </div>
        <strong>{tr("selectedCount", "{count} / {maximum} selected").replace("{count}", selectedCategoryIds.length).replace("{maximum}", limit)}</strong>
      </div>
      {searchEnabled && (
        <input
          className="third-category-search"
          value={search}
          disabled={disabled}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={tr("search", "Search categories")}
        />
      )}
      {(error || validationMessage) && <div className="third-category-error">{error || validationMessage}</div>}
      {loading ? <div className="third-category-state">{tr("loading", "Loading categories…")}</div> : (
        <div className="third-category-items">
          <SelectableOptionGrid
            options={items.map((category) => ({ value: category.id, label: category.name }))}
            selectedValues={selectedCategoryIds}
            onToggle={toggle}
            disabled={disabled}
          />
          {!items.length && <div className="third-category-state">{tr("noResults", "No third-level categories found.")}</div>}
        </div>
      )}
      {paginationEnabled && totalPages > 1 && (
        <nav className="third-category-pagination" aria-label={tr("pagination", "Category pagination")}>
          <button
            type="button"
            className="third-category-page-direction"
            disabled={page <= 1 || disabled}
            onClick={() => setPage((current) => current - 1)}
          >
            {tr("previous", "Previous")}
          </button>
          {pageNumbers.map((number) => <button type="button" className={number === page ? "active" : ""} key={number} onClick={() => setPage(number)}>{number}</button>)}
          <button
            type="button"
            className="third-category-page-direction"
            disabled={page >= totalPages || disabled}
            onClick={() => setPage((current) => current + 1)}
          >
            {tr("next", "Next")}
          </button>
          <span>{tr("pageOf", "Page {page} of {total}").replace("{page}", page).replace("{total}", totalPages)}</span>
        </nav>
      )}
    </section>
  );
};

export default ThirdLevelCategorySelector;
